import {
    requestQueue,
    fetchAll,
    httpGET,
    httpPOST,
    httpPATCH,
    httpDELETE,
    json,
} from './utils';
import { ACTIONS, ICONS } from '../const';
import findSelection from '../findSelection';
import { getContent, getLineData } from './content';
import {
    selectMarkupByPage,
    getMarkup,
    selectMarkupByCID,
    highlightsByContent,
} from '../selectors/markup';
import { getWordData, selectContent } from '../selectors/content';
import { selectUser } from '../selectors/current_user';
import {
    BEGIN,
    NEXT,
    COMMIT,
    REVERT,
    openTransaction,
} from '../reducers/transactions.js';
import R from 'ramda';
import { newNotification } from 'actions/notifications';

const { FAIL, SUCCESS, CANCEL } = ICONS;
const genKey = () => Math.floor(Math.random() * Math.pow(16, 6)).toString(16);

function toServer(getState, markup) {
    const content = selectContent(getState(), markup.content_id);

    return {
        color: markup.color,
        content: content.resource_uri,
        page: markup.page,
        position: (function() {
            const { type, x1, y1, x2, y2 } = markup;

            if (type == 'response') {
                return '';
            }

            // this is a page note, not a highlight. These could be 0s or could
            // be `== null`, checking falsy values covers both
            if (!x1 && !y1 && !x2 && !y2) {
                return '0,0';
            }

            return `${x1},${y1} ${x2},${y2}`;
        })(),
        section: `/api/v3/section/${content.section_id}/`,
        in_response_to: markup.in_response_to,
        is_question: markup.is_question,
        selection_text: markup.selection_text,
        note_text: markup.note_text,
        slug: content.slug,
        tags: markup.tags.join(','),
        type: markup.type,
    };
}

export const updateSelectionsForMarkup = (lineData, markup) => dispatch => {
    // This isn't a selection, so there's nothing to update. We can't use `x1`
    // and `y1` to verify because page notes are implemented as selections at
    // `0,0` without a pair of destination coordinates.
    if (markup.x2 == null && markup.y2 == null) {
        return;
    }
    if (lineData != null) {
        const selections = findSelection(lineData, markup);
        dispatch({
            type: ACTIONS.UPDATE_SELECTIONS,
            payload: {
                cid: markup.cid,
                selections,
            },
        });
    }
};

export const updateSelections = (content, pagenum, lineData) => (
    dispatch,
    getState
) => {
    const markups = selectMarkupByPage(getState(), content, pagenum);

    markups.forEach(m => dispatch(updateSelectionsForMarkup(lineData, m)));
};

export const update = (markup, updatedFields) => (dispatch, getState) => {
    const transaction = openTransaction();
    const revert = err => {
        console.log('revert: ', err); // eslint-disable-line no-console
        dispatch(
            newNotification(
                'Could not connect to server',
                'Your changes will not be saved',
                { dismissable: true, icon: FAIL }
            )
        );
        return dispatch(transaction(REVERT, { type: ACTIONS.NOOP }));
    };

    const content = selectContent(getState(), markup.content_id);
    const lineData = getWordData(getState(), content, markup.page);
    const index = markup.id != null ? markup.id : markup.cid;

    dispatch(transaction(BEGIN, { type: ACTIONS.NOOP }));

    function optimistic() {
        dispatch(
            transaction(COMMIT, {
                type: ACTIONS.UPDATE_MARKUP,
                payload: {
                    cid: markup.cid,
                    index,
                    fields: updatedFields,
                },
            })
        );
        updateSelectionsForMarkup(lineData, markup);
    }

    function request() {
        const { id } = Object.values(getState().markup.markup).find(
            ({ cid }) => cid === markup.cid
        );
        const body = toServer(getState, { ...markup, ...updatedFields });
        return httpPATCH(`/api/v3/note/${id}/`, body)
            .then(res => {
                if (!res.ok) {
                    throw new Error('Could not update markup');
                }
            }, revert)
            .catch(revert);
    }

    return requestQueue(markup.cid, optimistic, request).then(() =>
        dispatch(
            newNotification('Saved', 'Your note was saved successfully', {
                dismissable: false,
                icon: SUCCESS,
            })
        )
    );
};

export const fetchById = id => (dispatch, getState) => {
    const resource_uri = `/api/v3/note/${id}/`;
    httpGET(resource_uri)
        .then(json)
        .then(markup_data => {
            dispatch({
                type: ACTIONS.SYNC_MARKUP,
                payload: markup_data,
            });

            const markup = getMarkup(getState(), id);
            const content = selectContent(getState(), markup.content_id);
            const lineData = getWordData(getState(), content, markup.page);
            updateSelectionsForMarkup(lineData, markup);
        });
};

export const newMarkup = data => (dispatch, getState) => {
    const transaction = openTransaction();
    const cid = genKey();

    const revert = () => {
        dispatch(
            newNotification(
                'Could not connect to server',
                'Your changes will not be saved',
                { dismissable: true, icon: FAIL }
            )
        );
        dispatch(
            transaction(NEXT, {
                type: ACTIONS.DELETE_MARKUP,
                payload: cid,
            })
        );
        return dispatch(transaction(REVERT, { type: ACTIONS.NOOP }));
    };

    const content = selectContent(getState(), data.content_id);
    const lineData = getWordData(getState(), content, data.page);
    const me = selectUser(getState());

    function optimistic() {
        dispatch(
            transaction(BEGIN, {
                type: ACTIONS.NEW_MARKUP,
                payload: { ...data, cid },
                meta: {
                    author: me.v3_uri,
                    author_name: me.full_name,
                },
            })
        );
        const markup = selectMarkupByCID(getState(), cid);
        updateSelectionsForMarkup(lineData, markup);
    }

    function request() {
        const markup = getMarkup(getState(), cid);
        const serverData = toServer(getState, markup);

        return httpPOST('/api/v3/note/', serverData)
            .then(json)
            .then(data => {
                dispatch(
                    transaction(COMMIT, {
                        type: ACTIONS.SYNC_MARKUP,
                        payload: data,
                        meta: { cid },
                    })
                );
                const markup = selectMarkupByCID(getState(), cid);
                updateSelectionsForMarkup(lineData, markup);
            })
            .catch(revert);
    }

    requestQueue(cid, optimistic, request).then(() => {
        dispatch(
            newNotification(
                'Note added',
                'Your note was created successfully',
                {
                    dismissable: false,
                    icon: SUCCESS,
                }
            )
        );
    });
};

export const replyTo = (question, response) => (dispatch, getState) => {
    if (question.id == null) {
        console.warn('This should be a question, not an id'); // eslint-disable-line no-console
        question = getMarkup(getState(), question);
    }

    if (!question.is_question) {
        throw new Error('You can only reply to questions');
    }

    return dispatch(
        newMarkup({
            in_response_to: question.resource_uri,
            note_text: response.trim(),
            // V2 sets the student as the owner and the instructor becomes
            // the author.
            owner: question.owner,
            page: question.page,
            type: 'response',
            content_id: question.content_id,
        })
    );
};

export const deleteMarkup = markup => dispatch => {
    const transaction = openTransaction();

    dispatch(transaction(BEGIN, { type: ACTIONS.NOOP }));

    function optimistic() {
        dispatch(
            transaction(NEXT, {
                type: ACTIONS.DELETE_MARKUP,
                payload: markup.cid,
            })
        );
    }

    function request() {
        return httpDELETE(`/api/v3/note/${markup.id}/`).catch(err => {
            transaction(REVERT, { type: ACTIONS.NOOP });
        });
    }

    return requestQueue(markup.cid, optimistic, request).then(() => {
        dispatch(
            newNotification('Deleted', 'Your note was deleted', {
                dismissable: false,
                icon: CANCEL,
            })
        );
    });
};

export const fetchNoteCount = content => dispatch => {
    if (content.media_type != 'text') {
        return Promise.resolve(null);
    }
    // None is Python's version of null. This excludes any responses.
    // This regex (^.) matches any one character. The intention is to exclude
    // empty notes.
    const url = `${
        content.notes_uri
    }&in_response_to=None&note_text__iregex=${encodeURIComponent(
        '^.'
    )}&limit=1`;

    return httpGET(url)
        .then(json)
        .then(data => {
            dispatch({
                type: ACTIONS.NOTE_COUNT,
                payload: {
                    contentId: content.id,
                    total: data.meta.total_count,
                },
            });
        });
};

export const fetchUnansweredCount = content => dispatch => {
    const { section_id, slug, id } = content;
    let url = '/api/v3/question/?unanswered=true';
    url += `&section=${section_id}&slug=${slug}`;

    return httpGET(url)
        .then(json)
        .then(({ meta: { total_count } }) => {
            dispatch({
                type: ACTIONS.UNANSWERED_COUNT,
                payload: {
                    contentId: id,
                    total: total_count,
                },
            });
        });
};

const _fetchedPages = {};
export const getHighlights = (contentId, pagenum = undefined) => (
    dispatch,
    getState
) => {
    return dispatch(getContent(contentId)).then(content => {
        if (_fetchedPages[contentId] == null) {
            _fetchedPages[contentId] = {};
        }
        const fetched = _fetchedPages[contentId];

        const highlights = highlightsByContent(getState(), content);
        if (pagenum != null && fetched[pagenum]) {
            return highlights;
        }
        if (pagenum == null && fetched['ALL']) {
            return highlights;
        }

        let uri = `${content.notes_uri}&limit=75`;
        if (typeof pagenum === 'number') {
            dispatch(getLineData(contentId, pagenum));
            uri += `&page=${pagenum}`;
            fetched[pagenum] = true;
        } else {
            fetched['ALL'] = true;
        }

        return fetchAll(uri, bundle => {
            dispatch({
                type: ACTIONS.SYNC_MARKUP_BUNDLE,
                content_id: contentId,
                bundle: bundle,
            });

            const content = selectContent(getState(), contentId);
            const pagesToUpdate =
                pagenum == null
                    ? R.uniq(bundle.objects.map(m => m.page))
                    : [pagenum];

            pagesToUpdate.forEach(pagenum => {
                const lineData = getWordData(getState(), content, pagenum);
                if (lineData) {
                    dispatch(updateSelections(content, pagenum, lineData));
                }
            });
        });
    });
};



// WEBPACK FOOTER //
// ./src/actions/markup.js